import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Form,
  Select,
  Table,
  Tag,
  Dropdown,
  Switch,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { IconArrowRight } from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import api from "../../api";
import ConfirmModal from "../../components/modal/ConfirmModal";

const ROLE_LABEL = {
  OWNER: "Супер Админ",
  ADMIN: "Админ",
  EDITOR: "Редактор",
};

const ROLE_COLOR = {
  OWNER: "red",
  ADMIN: "blue",
  EDITOR: "green",
};

export default function Staff() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    userId: null,
    isActive: null,
  });

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState(undefined);
  const [page, setPage] = useState(1);

  // ─── Fetch ───────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const res = await api.users_list({
      page,
      limit: 10,
      ...(roleFilter && { role: roleFilter }),
    });
    if (res?.status === 200) {
      setRows(res.data.data);
      setMeta(res.data.meta);
    } else {
      toast("Хэрэглэгчид авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, [page, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ─── Client side search ──────────────────────────────
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(qq) ||
        r.email?.toLowerCase().includes(qq),
    );
  }, [rows, q]);

  // ─── Toggle active ───────────────────────────────────
  const handleToggle = async () => {
    const { userId, isActive } = confirmModal;
    setTogglingId(userId);
    setConfirmModal({ open: false, userId: null, isActive: null });

    const res = await api.users_toggle_active(userId);
    if (res?.status === 200) {
      setRows((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, isActive: res.data.isActive } : u,
        ),
      );
      toast(res.data.isActive ? "Идэвхжүүллээ" : "Идэвхгүй болголоо", "info");
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
    setTogglingId(null);
  };

  // ─── Create ──────────────────────────────────────────
  const onCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);

      const res = await api.users_create({
        name: values.name.trim(),
        email: values.email.trim(),
        password: values.password,
        role: values.role,
      });

      if (res?.status === 201) {
        toast("Ажилтан амжилттай нэмэгдлээ", "success");
        createForm.resetFields();
        setCreateOpen(false);
        fetchUsers();
      } else {
        toast(res?.data?.message || "Нэмэхэд алдаа гарлаа", "error");
      }
    } catch {
      // validation error
    } finally {
      setCreating(false);
    }
  };

  // ─── Edit open ───────────────────────────────────────
  const openEdit = (record) => {
    setEditingUser(record);
    editForm.setFieldsValue({
      name: record.name,
      email: record.email,
      role: record.role,
      password: "",
    });
    setEditOpen(true);
  };

  // ─── Update ──────────────────────────────────────────
  const onUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      setUpdating(true);

      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        role: values.role,
        // Нууц үг хоосон бол дамжуулахгүй
        ...(values.password && { password: values.password }),
      };

      const res = await api.users_update(editingUser.id, payload);

      if (res?.status === 200) {
        toast("Амжилттай засагдлаа", "success");
        setRows((prev) =>
          prev.map((u) =>
            u.id === editingUser.id ? { ...u, ...res.data } : u,
          ),
        );
        setEditOpen(false);
        setEditingUser(null);
      } else {
        toast(res?.data?.message || "Засахад алдаа гарлаа", "error");
      }
    } catch {
      // validation error
    } finally {
      setUpdating(false);
    }
  };

  // ─── Columns ─────────────────────────────────────────
  const columns = [
    {
      title: "Нэр",
      dataIndex: "name",
      key: "name",
      render: (v) => <div className="fw-semibold">{v}</div>,
    },
    {
      title: "Имэйл",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Эрх",
      dataIndex: "role",
      key: "role",
      render: (v) => <Tag color={ROLE_COLOR[v]}>{ROLE_LABEL[v] || v}</Tag>,
    },
    {
      title: "Сүүлд нэвтэрсэн",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      render: (v) => (v ? new Date(v).toLocaleString("mn-MN") : "-"),
    },
    {
      title: "Идэвхтэй",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          loading={togglingId === record.id}
          onChange={() =>
            setConfirmModal({
              open: true,
              userId: record.id,
              isActive: record.isActive,
            })
          }
        />
      ),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => new Date(v).toLocaleDateString("mn-MN"),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="staff-section">
      <PageHeader
        title="Ажилчид"
        sub="Системийн админ, ажилчдын жагсаалт"
        rSide={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateOpen(true);
            }}
          >
            Ажилтан нэмэх
          </Button>
        }
      />

      <div className="toolbar">
        <Input
          className="search"
          prefix={<SearchOutlined />}
          placeholder="Нэр / имэйл хайх..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
        />
        <Select
          className="status"
          placeholder="Эрхээр шүүх"
          allowClear
          value={roleFilter}
          onChange={(v) => {
            setRoleFilter(v);
            setPage(1);
          }}
          options={[
            { value: "OWNER", label: "Супер Админ" },
            { value: "ADMIN", label: "Админ" },
            { value: "EDITOR", label: "Редактор" },
          ]}
        />
      </div>

      <div className="table-wrap">
        <Table
          rowKey="id"
          loading={{ spinning: loading, size: "small" }}
          columns={columns}
          dataSource={filtered}
          pagination={{
            current: meta?.page,
            pageSize: meta?.limit,
            total: meta?.total,
            onChange: (p) => setPage(p),
          }}
        />
      </div>

      {/* ── Create Modal ── */}
      <Modal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="Нэмэх"
        cancelText="Болих"
        confirmLoading={creating}
        title="Ажилтан нэмэх"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэрээ оруулна уу" }]}
          >
            <Input placeholder="Жишээ: Bold Bataa" />
          </Form.Item>

          <Form.Item
            label="Имэйл"
            name="email"
            rules={[
              { required: true, message: "Имэйл оруулна уу" },
              { type: "email", message: "Имэйл формат буруу" },
            ]}
          >
            <Input placeholder="example@mail.com" />
          </Form.Item>

          <Form.Item
            label="Нууц үг"
            name="password"
            rules={[
              { required: true, message: "Нууц үг оруулна уу" },
              { min: 6, message: "Хамгийн багадаа 6 тэмдэгт" },
            ]}
          >
            <Input.Password placeholder="••••••" />
          </Form.Item>

          <Form.Item
            label="Эрх"
            name="role"
            rules={[{ required: true, message: "Эрх сонгоно уу" }]}
          >
            <Select
              placeholder="Сонгох"
              options={[
                { value: "ADMIN", label: "Админ" },
                { value: "EDITOR", label: "Редактор" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingUser(null);
        }}
        onOk={onUpdate}
        okText="Хадгалах"
        cancelText="Болих"
        confirmLoading={updating}
        title="Ажилтан засах"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэрээ оруулна уу" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Имэйл"
            name="email"
            rules={[
              { required: true, message: "Имэйл оруулна уу" },
              { type: "email", message: "Имэйл формат буруу" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Шинэ нууц үг"
            name="password"
            extra="Өөрчлөхгүй бол хоосон үлдээнэ үү"
            rules={[{ min: 6, message: "Хамгийн багадаа 6 тэмдэгт" }]}
          >
            <Input.Password placeholder="••••••" />
          </Form.Item>

          <Form.Item
            label="Эрх"
            name="role"
            rules={[{ required: true, message: "Эрх сонгоно уу" }]}
          >
            <Select
              options={[
                { value: "ADMIN", label: "Админ" },
                { value: "EDITOR", label: "Редактор" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModal
        showModal={confirmModal.open}
        setShowModal={(v) => setConfirmModal((prev) => ({ ...prev, open: v }))}
        submitHandler={handleToggle}
        title={
          confirmModal.isActive ? "Идэвхгүй болгох уу?" : "Идэвхжүүлэх үү?"
        }
        description={
          confirmModal.isActive
            ? "Та энэ ажилтны системд нэвтрэх эрхийг хаах гэж байна."
            : "Та энэ ажилтны системд нэвтрэх эрхийг дахин нээх гэж байна."
        }
        variant={confirmModal.isActive ? "warning" : "info"}
      />
    </section>
  );
}
